import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { userSignedUp } from "@/utils/tracking";
import { api } from "@/services/api";

import { AuthTransition } from "@/pages/authentication/components/AuthTransition";

/**
 * Where a signup verification email lands.
 *
 * THIS IS THE PAGE THAT HELD PEOPLE FOR FIVE SECONDS. It verified the email and
 * then said "You will be redirected to the onboarding process in 5 seconds",
 * counting down — and at least one user closed the window during it, which on a
 * signup is indistinguishable from abandoning it. Now a spinner and nothing
 * else. See AuthTransition.
 */
export const VerifyEmailMail = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  const [status, setStatus] = useState<"pending" | "success" | "error">("pending");
  // Only ever shown on failure — see AuthTransition.
  const [message, setMessage] = useState("");

  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (!token) {
      setStatus("error");
      setMessage("That verification link is missing its token.");
      return;
    }

    verifyEmail(token)
      .then(async () => {
        try {
          await api.users.updateOnboardingStep("signup/2");
        } catch (error) {
          // Non-fatal: the step is re-derived on the onboarding route, so a
          // failure here costs nothing beyond a log line. It must not block a
          // verified user from continuing.
          console.error("Failed to set initial onboarding step:", error);
        }

        setStatus("success");

        // Tracking after the status flip, not before: analytics must never sit
        // between a user and their destination.
        const signupTrackingData = localStorage.getItem("signup_tracking_data");
        if (signupTrackingData) {
          try {
            const trackingData = JSON.parse(signupTrackingData);
            userSignedUp({
              user_role: trackingData.user_role,
              signup_code: trackingData.signup_code,
              utm_source: trackingData.utm_source,
              utm_medium: trackingData.utm_medium,
              utm_campaign: trackingData.utm_campaign,
              ref_id: trackingData.ref_id ?? null,
            });
            localStorage.removeItem("signup_tracking_data");
          } catch (error) {
            console.error("Failed to parse signup tracking data:", error);
          }
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("This verification link is invalid or has already been used.");
      });
  }, [token, verifyEmail]);

  // Navigate once the exchange resolves. Separate from the request effect so
  // the spinner is never interrupted by a partially-applied state.
  useEffect(() => {
    if (status !== "success") return;
    navigate("/signup/2", { replace: true });
  }, [status, navigate]);

  return (
    <AuthTransition
      status={status}
      title="We couldn't verify your email"
      message={message}
      busyLabel="Verifying your email"
      action={
        <button
          type="button"
          onClick={() => navigate("/signup", { replace: true })}
          className="h-10 cursor-pointer rounded-lg bg-primary-500 px-5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          Back to signup
        </button>
      }
    />
  );
};
